// src/pages/BetaGate.jsx
//
// Private Beta gate — sits in front of the entire ODFTrades app.
// Visitors see a coming-soon page with a waitlist form + a founder
// access section. The founder enters the access code to unlock the
// real app for this browser (persisted in localStorage).
//
// SECURITY NOTE: this is a frontend password gate. It stops casual
// visitors but the password string IS in the JS bundle. For stronger
// protection later, move the check to a Supabase Edge Function.
//
import { useEffect, useRef, useState } from 'react';
import ODFLogo from '../components/ODFLogo';
import HeroCanvas from '../components/HeroCanvas';
import HeroAtmosphere from '../components/HeroAtmosphere';
import { supabase } from '../lib/supabase';
import { Lock, ArrowRight, CheckCircle2, Shield, ArrowLeft } from 'lucide-react';

const G = '#00e5a0';

// ── Access control ───────────────────────────────────────────────
// Password is NOT stored in the frontend. It lives only as a secret in
// the Supabase Edge Function `founder-auth`. The frontend POSTs the
// entered code; the function returns a random unlock token + expiry on
// success. We store that in localStorage and check expiry locally.
const UNLOCK_KEY     = 'odf_founder_unlock';
const FOUNDER_AUTH_FN = '/functions/v1/founder-auth';

export function isFounderUnlocked() {
  try {
    const raw = localStorage.getItem(UNLOCK_KEY);
    if (!raw) return false;
    const data = JSON.parse(raw);
    if (!data || !data.token || !data.expiresAt) return false;
    // Token expired? clear it and lock.
    if (new Date(data.expiresAt).getTime() <= Date.now()) {
      localStorage.removeItem(UNLOCK_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function unlockFounder(token, expiresAt) {
  try {
    localStorage.setItem(UNLOCK_KEY, JSON.stringify({ token, expiresAt }));
  } catch {}
}

export function lockFounder() {
  try { localStorage.removeItem(UNLOCK_KEY); } catch {}
}

// Waitlist endpoint — same Formspree inbox already used elsewhere on the site.
// Goes to info@odftrades.com.
const WAITLIST_FORMSPREE = 'https://formspree.io/f/mvzlawvw';

const EXPERIENCE_LEVELS = [
  'Less than 1 year',
  '1–2 years',
  '2–5 years',
  '5+ years',
  'Full-time / Professional',
];

// ── Component ────────────────────────────────────────────────────
export default function BetaGate({ onUnlock, onBack }) {
  const heroRef = useRef(null);

  // Waitlist state
  const [form, setForm] = useState({ name: '', email: '', experience: '', note: '' });
  const [waitlistStatus, setWaitlistStatus] = useState(''); // '' | 'sending' | 'success' | 'error'
  const [waitlistError,  setWaitlistError]  = useState('');

  // Founder login state
  const [showLogin, setShowLogin] = useState(false);
  const [password,  setPassword]  = useState('');
  const [loginErr,  setLoginErr]  = useState('');
  const [shake,     setShake]     = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const pwRef = useRef(null);

  useEffect(() => {
    if (showLogin && pwRef.current) pwRef.current.focus();
  }, [showLogin]);

  // ── Submit waitlist ────────────────────────────────────────────
  const submitWaitlist = async (e) => {
    e.preventDefault();
    setWaitlistError('');

    if (!form.email || !form.email.includes('@')) {
      setWaitlistStatus('error');
      setWaitlistError('Please enter a valid email address.');
      return;
    }
    setWaitlistStatus('sending');

    // 1. Email notification via Formspree (goes to info@odftrades.com)
    const payload = {
      name:       form.name || '(not provided)',
      email:      form.email,
      experience: form.experience || '(not provided)',
      note:       form.note || '(not provided)',
      source:     'ODFTrades Beta Gate Waitlist',
      subject:    'New ODFTrades Beta Waitlist Signup',
    };
    let formspreeOk = false;
    try {
      const res = await fetch(WAITLIST_FORMSPREE, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      formspreeOk = res.ok;
    } catch (_) {
      formspreeOk = false;
    }

    // 2. Best-effort Supabase store (silently no-ops if table doesn't exist)
    try {
      await supabase.from('waitlist').insert({
        name:       form.name,
        email:      form.email,
        experience: form.experience,
        note:       form.note,
        source:     'beta_gate',
      });
    } catch (_) { /* table may not exist yet — that's fine */ }

    if (formspreeOk) {
      setWaitlistStatus('success');
    } else {
      setWaitlistStatus('error');
      setWaitlistError('Could not send. Please email info@odftrades.com directly.');
    }
  };

  // ── Submit founder login (server-side validation) ──────────────
  // Posts the entered code to the Supabase Edge Function `founder-auth`.
  // The actual password lives ONLY as a Supabase secret, never in the
  // frontend bundle. On success we receive { token, expiresAt } and
  // store them locally as proof of access for this browser.
  const submitLogin = async (e) => {
    e.preventDefault();
    setLoginErr('');
    if (!password.trim()) return;
    setLoggingIn(true);

    try {
      const SUPABASE_URL = supabase.supabaseUrl;
      const SUPABASE_KEY = supabase.supabaseKey;
      const res = await fetch(`${SUPABASE_URL}${FOUNDER_AUTH_FN}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey':        SUPABASE_KEY,
          'Authorization': `Bearer ${SUPABASE_KEY}`,
        },
        body: JSON.stringify({ password: password.trim() }),
      });

      if (res.status === 401) {
        setLoginErr('Invalid access code');
        setPassword('');
        setShake(true);
        setTimeout(() => setShake(false), 450);
        if (pwRef.current) pwRef.current.focus();
        setLoggingIn(false);
        return;
      }
      if (!res.ok) {
        // 500 / network / misconfigured server
        setLoginErr('Could not reach the access service. Try again.');
        setLoggingIn(false);
        return;
      }
      const data = await res.json();
      if (!data?.token || !data?.expiresAt) {
        setLoginErr('Unexpected response. Try again.');
        setLoggingIn(false);
        return;
      }
      unlockFounder(data.token, data.expiresAt);
      onUnlock();
    } catch (err) {
      setLoginErr('Network error. Check your connection and try again.');
      setLoggingIn(false);
    }
  };

  // ── Styles ─────────────────────────────────────────────────────
  const inp = {
    width: '100%',
    background: '#0a0a0f',
    border: '1px solid #1e1e2e',
    borderRadius: 9,
    color: '#f0f0f5',
    padding: '11px 14px',
    fontSize: 14,
    fontFamily: "'IBM Plex Sans', sans-serif",
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  };
  const lbl = {
    fontSize: 11,
    color: '#8888a8',
    fontFamily: "'DM Mono', monospace",
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    display: 'block',
    marginBottom: 6,
  };
  const onFocus = e => { e.target.style.borderColor = G; };
  const onBlur  = e => { e.target.style.borderColor = '#1e1e2e'; };

  return (
    <div
      ref={heroRef}
      style={{
        minHeight: '100vh',
        background: '#0a0a0f',
        color: '#f0f0f5',
        fontFamily: "'IBM Plex Sans', sans-serif",
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Ambient layered background (same atmosphere as landing) */}
      <HeroCanvas />
      <HeroAtmosphere />

      {/* Top bar — logo (clickable → home) on left, back link + Private Beta badge on right */}
      <nav style={{
        position: 'relative', zIndex: 5,
        padding: '24px 32px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 16, flexWrap: 'wrap',
      }}>
        <ODFLogo size="compact" />
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <button
            onClick={() => {
              if (onBack) onBack();
              else {
                window.history.pushState(null, '', '/');
                window.dispatchEvent(new PopStateEvent('popstate'));
              }
            }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'transparent', border: '1px solid #2a2a3e',
              borderRadius: 100, padding: '6px 14px',
              fontSize: 12, color: '#8888a8', cursor: 'pointer',
              fontFamily: "'IBM Plex Sans',sans-serif",
              transition: 'border-color 0.2s, color 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = G; e.currentTarget.style.color = '#f0f0f5'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a3e'; e.currentTarget.style.color = '#8888a8'; }}
          >
            <ArrowLeft size={12} /> Back
          </button>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'rgba(0,229,160,0.07)',
            border: '1px solid rgba(0,229,160,0.22)',
            borderRadius: 100,
            padding: '6px 14px',
            fontSize: 11,
            color: G,
            fontFamily: "'DM Mono', monospace",
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}>
            <span style={{ position: 'relative', width: 6, height: 6, borderRadius: '50%', background: G }}>
              <span style={{ position: 'absolute', inset: -2, borderRadius: '50%', border: `1px solid ${G}`, animation: 'pulse-ring 1.9s ease-out infinite' }} />
            </span>
            Private Beta
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div style={{
        position: 'relative', zIndex: 5,
        padding: '40px 24px 32px',
        textAlign: 'center',
        maxWidth: 760,
        margin: '0 auto',
      }}>
        <div style={{
          fontSize: 11, color: '#55556a', fontFamily: "'DM Mono', monospace",
          textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 16,
        }}>
          ODFTrades Performance Lab · Founder Testing Phase
        </div>
        <h1 style={{
          fontSize: 'clamp(34px, 5.5vw, 64px)',
          fontWeight: 600,
          letterSpacing: '-0.035em',
          lineHeight: 1.08,
          margin: '0 0 22px',
        }}>
          <span style={{ color: G }}>ODFTrades</span> Performance Lab,
          <br />
          built quietly. First.
        </h1>
        <p style={{
          fontSize: 15.5,
          color: '#8888a8',
          maxWidth: 640,
          margin: '0 auto 16px',
          lineHeight: 1.85,
        }}>
          Performance Lab is the main trading performance product built by ODFTrades — for traders who
          want to journal trades, review execution, identify behavioural patterns, track mistakes, and
          turn raw trading data into clearer rules for improvement.
        </p>
        <p style={{
          fontSize: 14,
          color: '#55556a',
          maxWidth: 620,
          margin: '0 auto',
          lineHeight: 1.85,
        }}>
          The platform is currently in private founder testing — being used live in real
          trading sessions to refine the flow and decide what stays, what changes, and
          what ships at public launch. Join the waitlist for early access.
        </p>
      </div>

      {/* Two-card grid */}
      <div className="beta-grid" style={{
        position: 'relative', zIndex: 5,
        maxWidth: 880, margin: '0 auto', padding: '24px',
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20,
      }}>
        {/* Waitlist card */}
        <div style={{
          background: 'linear-gradient(180deg, rgba(22,22,31,0.85), rgba(13,13,20,0.85))',
          border: '1px solid #1e1e2e',
          borderRadius: 16,
          padding: '28px 28px 26px',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          boxShadow: '0 18px 50px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04)',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: 0, left: 20, right: 20, height: 1, background: `linear-gradient(90deg, transparent, ${G}66, transparent)` }} />
          <div style={{ fontSize: 11, color: G, fontFamily: "'DM Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 12 }}>
            Join the waitlist
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', margin: '0 0 6px' }}>
            Get early access
          </h2>
          <p style={{ fontSize: 13, color: '#8888a8', margin: '0 0 22px', lineHeight: 1.65 }}>
            Be first in line when private beta opens to selected traders.
          </p>

          {waitlistStatus === 'success' ? (
            <div style={{
              padding: '20px 18px',
              background: 'rgba(0,229,160,0.06)',
              border: '1px solid rgba(0,229,160,0.25)',
              borderRadius: 10,
              display: 'flex', alignItems: 'center', gap: 14,
            }}>
              <div style={{
                width: 38, height: 38, borderRadius: '50%',
                background: 'rgba(0,229,160,0.12)',
                border: `1px solid ${G}45`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: G, flexShrink: 0,
              }}>
                <CheckCircle2 size={18} strokeWidth={1.8} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: G, marginBottom: 3 }}>You're on the list</div>
                <div style={{ fontSize: 12, color: '#8888a8', lineHeight: 1.6 }}>
                  We'll reach out from info@odftrades.com when access opens.
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={submitWaitlist} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={lbl} htmlFor="bg-name">Name</label>
                <input id="bg-name" type="text" value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="Your name"
                  style={inp} onFocus={onFocus} onBlur={onBlur} />
              </div>
              <div>
                <label style={lbl} htmlFor="bg-email">Email</label>
                <input id="bg-email" type="email" value={form.email} required
                  onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="your@email.com"
                  style={inp} onFocus={onFocus} onBlur={onBlur} />
              </div>
              <div>
                <label style={lbl} htmlFor="bg-exp">Trading experience</label>
                <select id="bg-exp" value={form.experience}
                  onChange={e => setForm(p => ({ ...p, experience: e.target.value }))}
                  style={{ ...inp, appearance: 'none', cursor: 'pointer' }}
                  onFocus={onFocus} onBlur={onBlur}>
                  <option value="">— Select —</option>
                  {EXPERIENCE_LEVELS.map(x => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl} htmlFor="bg-note">What do you want ODFTrades to help you improve? (optional)</label>
                <textarea id="bg-note" rows={3} value={form.note}
                  onChange={e => setForm(p => ({ ...p, note: e.target.value }))}
                  placeholder="e.g. revenge trading, sizing, session selection..."
                  style={{ ...inp, resize: 'vertical', minHeight: 76 }}
                  onFocus={onFocus} onBlur={onBlur} />
              </div>

              {waitlistError && (
                <div style={{ fontSize: 12, color: '#ff4d6d', fontFamily: "'DM Mono', monospace" }}>
                  {waitlistError}
                </div>
              )}

              <button type="submit" disabled={waitlistStatus === 'sending'} style={{
                background: G, color: '#0a0a0f',
                border: 'none', borderRadius: 9, padding: '13px',
                fontSize: 14, fontWeight: 700,
                cursor: waitlistStatus === 'sending' ? 'wait' : 'pointer',
                fontFamily: "'IBM Plex Sans', sans-serif",
                transition: 'background 0.2s, transform 0.1s',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
                onMouseEnter={e => { if (waitlistStatus !== 'sending') e.currentTarget.style.background = '#00c98a'; }}
                onMouseLeave={e => e.currentTarget.style.background = G}>
                {waitlistStatus === 'sending' ? 'Sending...' : <>Join Waitlist <ArrowRight size={15} /></>}
              </button>
              <div style={{ fontSize: 11, color: '#55556a', fontFamily: "'DM Mono', monospace", textAlign: 'center' }}>
                No spam. Unsubscribe anytime.
              </div>
            </form>
          )}
        </div>

        {/* Founder Access card */}
        <div style={{
          background: 'linear-gradient(180deg, rgba(22,22,31,0.85), rgba(13,13,20,0.85))',
          border: '1px solid #1e1e2e',
          borderRadius: 16,
          padding: '28px 28px 26px',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          boxShadow: '0 18px 50px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04)',
          position: 'relative', overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ position: 'absolute', top: 0, left: 20, right: 20, height: 1, background: `linear-gradient(90deg, transparent, ${G}66, transparent)` }} />
          <div style={{ fontSize: 11, color: '#8888a8', fontFamily: "'DM Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Lock size={11} /> Founder Access
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', margin: '0 0 6px' }}>
            Private login
          </h2>
          <p style={{ fontSize: 13, color: '#8888a8', margin: '0 0 22px', lineHeight: 1.65 }}>
            Enter the founder access code to continue into the platform.
          </p>

          {!showLogin ? (
            <button onClick={() => setShowLogin(true)} style={{
              background: 'transparent',
              border: '1px solid #2a2a3e',
              color: '#f0f0f5',
              borderRadius: 9,
              padding: '12px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: "'IBM Plex Sans', sans-serif",
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              transition: 'border-color 0.2s, background 0.2s',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = G; e.currentTarget.style.background = 'rgba(0,229,160,0.05)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a3e'; e.currentTarget.style.background = 'transparent'; }}>
              <Shield size={14} /> Enter Access Code
            </button>
          ) : (
            <form onSubmit={submitLogin}
              style={{
                display: 'flex', flexDirection: 'column', gap: 12,
                animation: shake ? 'shake 0.45s' : 'none',
              }}>
              <div>
                <label style={lbl} htmlFor="bg-pw">Access Code</label>
                <input
                  ref={pwRef}
                  id="bg-pw"
                  type="password"
                  value={password}
                  onChange={e => { setPassword(e.target.value); if (loginErr) setLoginErr(''); }}
                  placeholder="••••••••"
                  autoComplete="off"
                  style={{ ...inp, borderColor: loginErr ? '#ff4d6d' : '#1e1e2e', letterSpacing: '0.08em' }}
                  onFocus={onFocus}
                  onBlur={onBlur}
                />
              </div>
              {loginErr && (
                <div style={{ fontSize: 12, color: '#ff4d6d', fontFamily: "'DM Mono', monospace" }}>
                  {loginErr}
                </div>
              )}
              <button type="submit" disabled={loggingIn} style={{
                background: G, color: '#0a0a0f',
                border: 'none', borderRadius: 9, padding: '13px',
                fontSize: 14, fontWeight: 700,
                cursor: loggingIn ? 'wait' : 'pointer',
                opacity: loggingIn ? 0.75 : 1,
                fontFamily: "'IBM Plex Sans', sans-serif",
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'background 0.2s',
              }}
                onMouseEnter={e => { if (!loggingIn) e.currentTarget.style.background = '#00c98a'; }}
                onMouseLeave={e => { e.currentTarget.style.background = G; }}>
                {loggingIn ? 'Verifying...' : <>Unlock Platform <ArrowRight size={15} /></>}
              </button>
              <div style={{ fontSize: 11, color: '#55556a', fontFamily: "'DM Mono', monospace", textAlign: 'center' }}>
                Founder only · Case-sensitive · Validated server-side
              </div>
            </form>
          )}

          {/* Spacer + status note */}
          <div style={{
            marginTop: 'auto',
            paddingTop: 22,
            fontSize: 11,
            color: '#55556a',
            fontFamily: "'DM Mono', monospace",
            lineHeight: 1.7,
          }}>
            Public release pending. Selected traders will be invited from the waitlist.
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer style={{
        position: 'relative', zIndex: 5,
        padding: '40px 32px 28px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14,
        maxWidth: 880, margin: '20px auto 0',
        borderTop: '1px solid #1a1a26',
      }}>
        <div style={{ fontSize: 11, color: '#2a2a3e', fontFamily: "'DM Mono', monospace" }}>
          © {new Date().getFullYear()} ODFTrades Inc. · Educational trading platform & software company
        </div>
        <div style={{ fontSize: 11, color: '#55556a', fontFamily: "'DM Mono', monospace" }}>
          info@odftrades.com
        </div>
      </footer>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%      { transform: translateX(-8px); }
          40%      { transform: translateX(8px); }
          60%      { transform: translateX(-5px); }
          80%      { transform: translateX(5px); }
        }
        @media (max-width: 720px) {
          .beta-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
